// Parse a spoken/typed Calcutta line into { teamId, bid, owner } for the auction
// runner (PC) AND the phone remote. Deterministic, client-side — no API key, and
// it NEVER commits: it only fills the editable fields. Preferred input format is
// "Team, Owner, Bid" (comma-separated), e.g.
//   "Langford Gunter, Bob Anderson, forty-two hundred"
// but it falls back to best-effort when commas are missing.

const ONES = { zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19 }
const TENS = { twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90 }

// "forty-two hundred" → 4200, "four thousand two hundred" → 4200, "4200" → 4200,
// "five grand" → 5000. Returns NaN when there's no number.
export function wordsToNumber(raw) {
  if (raw == null) return NaN
  const s = String(raw).toLowerCase().replace(/[$,]/g, ' ').replace(/\band\b/g, ' ').replace(/-/g, ' ').trim()
  if (!s) return NaN
  if (/^[0-9\s]+$/.test(s)) { const d = s.replace(/[^0-9]/g, ''); return d ? Number(d) : NaN }
  const tokens = s.split(/\s+/).filter(Boolean)
  let total = 0, cur = 0, saw = false
  for (const tk of tokens) {
    if (tk in ONES) { cur += ONES[tk]; saw = true }
    else if (tk in TENS) { cur += TENS[tk]; saw = true }
    else if (tk === 'hundred') { cur = (cur || 1) * 100; saw = true }
    else if (tk === 'thousand' || tk === 'grand' || tk === 'k') { cur = (cur || 1) * 1000; total += cur; cur = 0; saw = true }
    else if (/^\d+$/.test(tk)) { cur += Number(tk); saw = true }
  }
  const val = total + cur
  return saw && val > 0 ? val : NaN
}

const lastName = (full) => String(full || '').trim().split(/\s+/).pop().toLowerCase()
const titleCase = (s) => String(s || '').trim().replace(/\s+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

// Fuzzy-match a spoken team phrase to a team in the flight, by player last names
// or the team name. teams = [{ id, name, players }]. Returns the best team or null.
export function matchTeam(phrase, teams) {
  const p = String(phrase || '').toLowerCase().replace(/[^a-z\s]/g, ' ')
  if (!p.trim() || !teams?.length) return null
  const words = new Set(p.split(/\s+/).filter(Boolean))
  let best = null, bestScore = 0
  for (const t of teams) {
    let score = 0
    const nameWords = String(t.name || '').toLowerCase().replace(/[^a-z\s]/g, ' ').split(/\s+/).filter(Boolean)
    for (const w of nameWords) if (words.has(w)) score += 1
    for (const pl of (t.players || [])) { if (words.has(lastName(pl))) score += 2 }
    if (score > bestScore) { bestScore = score; best = t }
  }
  return bestScore > 0 ? best : null
}

// Returns true when the line is a "finish this flight" command.
export function isFinishCommand(text) {
  return /\b(finish|close|done with)\b.*\bflight\b|\bfinish flight\b|^\s*finish\s*$/i.test(String(text || ''))
}

// Parse a full line into { teamId, matchedName, bid, owner }. Nothing is committed.
export function parseCalcuttaLine(text, teams) {
  const raw = String(text || '').trim()
  if (!raw) return { teamId: null, matchedName: '', bid: null, owner: '' }
  const parts = raw.split(/[,;]|\bfor\b/i).map((s) => s.trim()).filter(Boolean)
  let teamPart = '', ownerPart = '', bidPart = ''
  if (parts.length >= 3) { teamPart = parts[0]; ownerPart = parts[1]; bidPart = parts[2] }
  else if (parts.length === 2) {
    teamPart = parts[0]
    if (!Number.isNaN(wordsToNumber(parts[1]))) bidPart = parts[1]; else ownerPart = parts[1]
  } else { teamPart = parts[0] }
  let bid = bidPart ? wordsToNumber(bidPart) : NaN
  if (Number.isNaN(bid)) { const m = raw.match(/\$?\s*[\d,]{2,}/); if (m) bid = wordsToNumber(m[0]) }
  const matched = matchTeam(teamPart || raw, teams)
  return { teamId: matched?.id || null, matchedName: matched?.name || '', bid: Number.isNaN(bid) ? null : bid, owner: titleCase(ownerPart) }
}
